import React, { useState, useEffect } from 'react';
import { 
  QrCodeIcon, 
  ChatBubbleOvalLeftEllipsisIcon,
  LinkIcon 
} from '@heroicons/react/24/outline';
import { customerApi } from '../../api/customerApi';
import { paymentApi } from '../../api/paymentApi';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Payments = () => {
  const [pendingCustomers, setPendingCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [requestingPayment, setRequestingPayment] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerApi.getCustomers();
      const allCustomers = response.data.data || [];
      // Filter customers who owe us money (balance > 0)
      const pending = allCustomers.filter(c => c.currentBalance > 0);
      setPendingCustomers(pending);
    } catch (error) {
      console.error('Error fetching customers for reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (customer) => {
    if (!customer.phone) return alert('No phone number for this customer');
    
    const loadingToast = toast.loading('Generating payment link...');
    let paymentLink = '';
    
    try {
      const res = await paymentApi.createPaymentOrder({
        customerId: customer._id,
        amount: customer.currentBalance
      });
      if (res.data.success && res.data.data.paymentLink) {
        paymentLink = res.data.data.paymentLink;
      }
      toast.dismiss(loadingToast);
    } catch (err) {
      console.warn("Failed to generate payment link", err);
      toast.dismiss(loadingToast);
    }

    let message = `Hello ${customer.name}, your payment of Rs. ${customer.currentBalance} is pending. Please clear your dues at the earliest.`;
    if (paymentLink) {
      message += `\n\nYou can pay securely online using this link: ${paymentLink}\n\nThanks!`;
    }

    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/91${customer.phone}?text=${encodedMessage}`;
    window.open(url, '_blank');
  };

  const remindAll = () => {
    if (pendingCustomers.length === 0) return alert('No pending recoveries');
    alert(`This will bulk message ${pendingCustomers.length} customers via a WhatsApp API in the future. For now, click individually.`);
  };

  const handleRequestPayment = async (e) => {
    e.preventDefault();
    if (!paymentAmount || Number(paymentAmount) <= 0 || !selectedCustomer) return;
    
    try {
      setRequestingPayment(true);
      const res = await paymentApi.createPaymentOrder({
        customerId: selectedCustomer._id,
        amount: Number(paymentAmount)
      });
      if (res.data.success) {
        toast.success('Payment Request generated successfully!');
        setShowPaymentModal(false);
        setPaymentAmount('');
        window.open(res.data.data.paymentLink, '_blank');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to request payment');
    } finally {
      setRequestingPayment(false);
    }
  };
  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Payments & Reminders</h1>
          <p className="text-[15px] font-medium text-gray-500 dark:text-gray-400 mt-1">
            Accept online payments and send reminders to customers.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* QR Code Section */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] text-center flex flex-col items-center transition-colors">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Your Business QR</h2>
            <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-3xl border-2 border-dashed border-gray-200 dark:border-gray-700 mb-6 transition-colors">
              <QrCodeIcon className="w-40 h-40 text-gray-300 dark:text-gray-600 mx-auto" />
              <p className="text-[12px] font-bold text-gray-400 dark:text-gray-500 mt-4 tracking-widest uppercase">Scan to Pay</p>
            </div>
            <button className="w-full bg-[#dc343b] text-white py-3.5 rounded-xl font-bold text-[14px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20">
              Download QR Code
            </button>
          </div>
        </div>

        {/* Reminders Section */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden transition-colors">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Pending Recoveries</h2>
              <button 
                onClick={remindAll}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl font-bold text-[13px] hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 shadow-sm"
              >
                <ChatBubbleOvalLeftEllipsisIcon className="w-4 h-4" />
                Remind All
              </button>
            </div>
            
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {loading ? (
                <div className="p-10 text-center text-gray-500 font-medium text-sm">Loading...</div>
              ) : pendingCustomers.length === 0 ? (
                <div className="p-10 text-center text-gray-500 font-medium text-sm">No pending recoveries. Awesome!</div>
              ) : pendingCustomers.map((customer) => (
                <div key={customer._id} className="p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-500 font-bold text-[15px] shadow-sm shrink-0">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-gray-900 dark:text-white truncate max-w-[150px] sm:max-w-full">{customer.name}</h3>
                      <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400 mt-0.5">{customer.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto justify-between sm:justify-end">
                    <span className="text-[16px] font-extrabold text-red-500 dark:text-red-400">₹{customer.currentBalance.toLocaleString()}</span>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => sendReminder(customer)}
                        className="bg-[#25D366] text-white p-2.5 rounded-xl hover:bg-[#1DA851] transition-colors shadow-sm" 
                        title="Send WhatsApp Reminder"
                      >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
                      </svg>
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setPaymentAmount(customer.currentBalance);
                        setShowPaymentModal(true);
                      }}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" 
                      title="Send Payment Link"
                    >
                      <LinkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Payment Request Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowPaymentModal(false)}></div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[18px] font-bold text-gray-900 dark:text-white">Request Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleRequestPayment}>
              <div className="mb-6">
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Amount to Request (₹)</label>
                <input
                  type="number"
                  required
                  min="1"
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[16px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors font-bold"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                />
                <p className="text-[12px] text-gray-500 mt-2">
                  A payment link will be generated for {selectedCustomer?.name}. They can pay via UPI, Credit/Debit Card, or Netbanking.
                </p>
              </div>
              
              <button
                type="submit"
                disabled={requestingPayment}
                className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white py-3.5 rounded-xl font-extrabold text-[15px] transition-colors shadow-sm disabled:opacity-70 flex justify-center items-center"
              >
                {requestingPayment ? 'Generating Link...' : 'Generate Payment Link'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
